import { createInitialState } from "../app/state/AppState";
import { StateStore } from "../app/state/StateStore";
import { loadState } from "../app/state/persistence";
import type { AppState } from "../app/state/AppState";

import { ApiClient, type Screen } from "../app/api/ApiClient";
import { AuthManager } from "../app/auth/AuthManager";

import type { Screen as AppScreen } from "../screens/Screen";
import { CoreScreen } from "../screens/CoreScreen";

import type { RenderHost } from "../render/RenderHost";
import { RenderEngine } from "../render/RenderEngine";


import { BlockRegistry } from "../blocks/BlockRegistry";
import { registerBlocks } from "../blocks/registerBlocks";
import type { BlockServices } from "../blocks/BlockServices";

import { ImageViewer } from "../services/ImageViewer";
import { NavigationController } from "./NavigationController";
import { ScreenFactory } from "./ScreenFactory";

import { ScreenPresenter } from "../screens/ScreenPresenter";
import { ScreenMapperRegistry } from "../screens/mappers/ScreenMapperRegistry";
import { registerScreenMappers } from "../screens/mappers/registerScreenMappers";



const BASE_URL = "https://german.ntbooks.pro";

/**
 * FrontendApplication
 *
 * Composition root приложения.
 * Создает сервисы и координирует пользовательские сценарии.
 */
export class FrontendApplication {

    private readonly root: HTMLElement;

    private readonly store: StateStore;

    private readonly auth: AuthManager;

    private readonly api: ApiClient;

    private readonly renderHost: RenderHost;

    private readonly blockRegistry: BlockRegistry;

    private readonly blockServices: BlockServices;

    private readonly imageViewer: ImageViewer;

    private readonly navigation: NavigationController;

    private readonly screenFactory: ScreenFactory;

    private readonly screenPresenter: ScreenPresenter;

    private readonly screenMappers: ScreenMapperRegistry;

    private currentScreen: AppScreen | null = null;

    constructor(root: HTMLElement) {

        this.root = root;

        const persisted = loadState();


        const initialState = createInitialState();

        if (persisted) {

            // 👉 восстанавливаем только если это тот же крок
            if (persisted.currentKrokId) {

                initialState.currentKrokId = persisted.currentKrokId;
                initialState.gaps = persisted.gaps || {};
                initialState.revealed = persisted.revealed || {};
            }

            initialState.scrollY =
                persisted.scrollY || 0;
        }

        this.store =
            new StateStore(initialState);

        this.auth =
            new AuthManager(BASE_URL);

        this.api =
            new ApiClient(BASE_URL, this.auth);

        this.imageViewer =
              new ImageViewer();    

        this.blockServices = {

            openImage: (src: string) => {

                this.imageViewer.open(src);

            }

        };

        this.blockRegistry =
                new BlockRegistry();

        registerBlocks(
                this.blockRegistry,
                this.blockServices
            ); 

        this.screenFactory =
            new ScreenFactory(

                this.store,

                this.blockRegistry,

                this.blockServices

            );


        this.navigation =
            new NavigationController(

                this.api,

                this.auth,

                this.store,

                this.applyScreen.bind(this)

            );


        (window as any).auth = this.auth;

        this.renderHost =
            this.createRenderEngine();

        this.screenPresenter =
            new ScreenPresenter(
                this.renderHost
            );  

        this.screenMappers =
            new ScreenMapperRegistry();

        registerScreenMappers(
            this.screenMappers,
            this.store
        );      
    }

    public start(): void {

        this.store.subscribe(() => {

            if (!this.currentScreen) {
                return;
            }

            this.currentScreen.update();

            this.renderHost.showScreen(
                this.currentScreen
            );

        });

        if (this.currentScreen) {

            this.currentScreen.mount();

            this.renderHost.showScreen(
                this.currentScreen
            );

        }

        void this.boot();

    }

    private setCurrentScreen(
        screen: AppScreen,
        state?: AppState
    ): void {

        this.screenPresenter.show(
            this.currentScreen,
            screen
        );

        this.currentScreen = screen;

        if (state) {

            this.store.replace(state);

        }

    }

    private createScreen(
        screen: Screen
    ): AppScreen {

        const subscriptionActions = {

            subscribe: (
                stickerId: string
            ) => {

                void (async () => {

                    try {

                        const result =
                            await this.api.subscribe(
                                stickerId,
                                "UAH"
                            );

                        if ((result as any).screen) {

                            this.applyScreen(
                                (result as any).screen
                            );

                            return;

                        }

                        if ((result as any).redirect_url) {

                            window.location.href =
                                (result as any).redirect_url;

                            return;

                        }

                        console.error(
                            "Unexpected subscribe response",
                            result
                        );

                    } catch (e) {

                        console.error(e);

                        alert(
                            "Помилка оформлення підписки"
                        );

                    }

                })();

            }

        };

        const subscriptionChangeActions = {

            confirmChange: (
                stickerId: string
            ) => {

                void (async () => {

                    try {

                        const result =
                            await this.api.confirmChange(
                                stickerId
                            );

                        if ((result as any).screen) {

                            this.applyScreen(
                                (result as any).screen
                            );

                            return;

                        }

                        console.error(
                            "Unexpected confirmChange response",
                            result
                        );

                    } catch (e) {

                        console.error(e);

                        alert(
                            "Помилка відкриття підтвердження зміни підписки"
                        );

                    }

                })();

            },


            confirmSubscriptionChange: (

                subscriptionId: string,

                stickerId: string

            ) => {

                void (async () => {

                    try {

                        const result =
                            await this.api.confirmChangeSubscription(

                                subscriptionId,

                                stickerId

                            );

                        if (result.page_url) {

                            window.location.href =
                                result.page_url;

                            return;

                        }

                        console.error(

                            "Unexpected confirmSubscriptionChange response",

                            result

                        );

                    } catch (e) {

                        console.error(e);

                        alert(
                            "Помилка оформлення зміни підписки"
                        );

                    }

                })();

            }



        };

              return this.screenFactory.create(

            screen,

            {

                core: {

                    completeClicked:
                        this.navigation.completeClicked.bind(this.navigation),

                    prevClicked:
                        this.navigation.prevClicked.bind(this.navigation),

                    nextClicked:
                        this.navigation.nextClicked.bind(this.navigation),

                    entryClicked:
                        this.navigation.entryClicked.bind(this.navigation),

                    listClicked:
                        this.onListClick.bind(this),

                    userClicked:
                        this.onUserClick.bind(this)

                },

                header: {

                    listClicked:
                        this.onListClick.bind(this),

                    userClicked:
                        this.onUserClick.bind(this),

                    backClicked:
                        this.onBackClick.bind(this),

                    logoutClicked:
                        this.onLogout.bind(this)

                },

                list: {

                    openKrok:
                        this.navigation.openKrok.bind(
                            this.navigation
                        )

                },

                auth: {

                    submit: async (
                        email: string
                    ) => {

                        await this.auth.requestOtp(

                            email,

                            this.store.getState().referral

                        );

                        this.applyScreen({

                            type: "otp",

                            email

                        });

                    },

                    submitOtp: async (

                        email: string,

                        otp: string

                    ) => {

                        await this.auth.confirmOtp(

                            email,

                            otp

                        );

                        const state =
                            this.store.getState();

                        if (state.selectedSticker) {

                            const result =
                                await this.api.subscribe(

                                    state.selectedSticker,

                                    "UAH"

                                );

                            if ((result as any).screen) {

                                this.applyScreen(
                                    (result as any).screen
                                );

                                return;

                            }

                            if ((result as any).redirect_url) {

                                window.location.href =
                                    (result as any).redirect_url;

                                return;

                            }

                            throw new Error(
                                "Unexpected subscribe response"
                            );

                        }

                        const res =
                            await this.api.getEntry();

                        this.applyScreen(
                            res.screen
                        );

                    },

                    resendOtp: async (
                        email: string
                    ) => {

                        await this.auth.requestOtp(
                            email
                        );

                    }

                },


                account: {

                    openKrok: (krokId: string) => {

                        this.navigation.openKrok(
                            krokId
                        );

                    },

                    openSubscriptions: (
                        productId: string
                    ) => {

                        void (async () => {

                            try {

                                const result =
                                    await this.api.getSubscription(
                                        productId
                                    );

                                this.applyScreen(
                                    result.screen
                                );

                            } catch (e) {

                                console.error(e);

                                alert(
                                    "Помилка завантаження підписок"
                                );

                            }

                        })();

                    },

                    confirmCancelSubscription: (
                        subscriptionId: string
                    ) => {

                        const products =
                            this.store.getState()
                                .accountData?.products ?? [];

                        let selected: {

                            product: any;

                            subscription: any;

                        } | null = null;

                        for (const product of products) {

                            const subscription =
                                product.subscriptions.find(

                                    s =>
                                        s.subscription_id ===
                                        subscriptionId

                                );

                            if (subscription) {

                                selected = {

                                    product,

                                    subscription

                                };

                                break;

                            }

                        }

                        if (!selected) {

                            console.error(
                                "Subscription not found:",
                                subscriptionId
                            );

                            return;

                        }

                        const newState = {

                            ...this.store.getState(),

                            selectedSubscriptionId:
                                subscriptionId,

                            selectedSubscription:
                                selected

                        };

                        this.setCurrentScreen(

                            this.createScreen({

                                type:
                                    "confirmCancelSubscription"

                            } as any),

                            newState

                        );

                    },

                    cancelSubscription: (
                        subscriptionId: string
                    ) => {

                        void (async () => {

                            try {

                                await this.api.cancelSubscription(
                                    subscriptionId
                                );

                                const screen =
                                        await this.api.getAccount();

                                    this.applyScreen(
                                        screen
                                    );

                            } catch (e) {

                                console.error(e);

                                alert(
                                    "Помилка скасування підписки"
                                );

                            }

                        })();

                    },

                    copyLandingLink: (
                        title: string,
                        url: string
                    ) => {

                        void (async () => {

                            try {

                                await navigator.clipboard.writeText(
                                    url
                                );

                                alert(
                                    `Посилання на "${title}" скопійовано, вставте його і відправте у зручний вам спосіб (Telegram, Viber, e-mail тощо)`
                                );

                            } catch (e) {

                                console.error(e);

                                alert(
                                    "Не вдалося скопіювати посилання."
                                );

                            }

                        })();

                    },

                    openLanding: (
                        url: string
                    ) => {

                        window.open(
                            url,
                            "_blank"
                        );

                    },

                    back: () => {

                        this.onBackClick();

                    }

                    },

                subscription: {

                    subscribe: (
                        stickerId: string
                    ) => {

                        void (async () => {

                            try {

                                const result =
                                    await this.api.subscribe(
                                        stickerId,
                                        "UAH"
                                    );

                                window.location.href =
                                    result.redirect_url;

                            } catch (e) {

                                console.error(e);

                                alert(
                                    "Помилка оформлення підписки"
                                );

                            }

                        })();

                    }

                }

            },

            subscriptionActions,

            subscriptionChangeActions

        );

    }
    // ============================================================
    // ROUTE PARSER
    // ============================================================

    private getInitialRoute() {

        const params =
            new URLSearchParams(
                window.location.search
            );

        // Сохраняем только маркетинговые параметры
        const referral =
            new URLSearchParams(params);

        referral.delete("auth");
        referral.delete("sticker");

        return {

            isAuth:
                params.has("auth"),

            sticker:
                params.get("sticker"),

            referral:
                referral.toString()
        };
    }

        // ============================================================
        // APPLY SCREEN
        // ============================================================

        private applyScreen(
            screen: Screen
        ): void {

            if (screen.type === "krok") {

                this.applyKrokScreen(
                    screen
                );

                return;

            }

            this.applyMappedScreen(
                screen
            );

        }
        // ============================================================
        // APPLY MAPPED SCREEN
        // ============================================================

        private applyMappedScreen(
            screen: Screen
        ): void {

            const mapper =
                this.screenMappers.get(
                    screen.type
                );

            mapper.apply(
                screen
            );

            this.setCurrentScreen(
                this.createScreen(screen)
            );

        }

        // ============================================================
        // APPLY AUTH SCREEN
        // ============================================================

        private applyAuthScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // ============================================================
        // APPLY SUBSCRIBE AUTH SCREEN
        // ============================================================

        private applySubscribeAuthScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }
        // ============================================================
        // APPLY OTP SCREEN
        // ============================================================

        private applyOtpScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }
        // ============================================================
        // APPLY KROK SCREEN
        // ============================================================

        private applyKrokScreen(
            screen: KrokScreen
        ): void {

            this.screenMappers
                .get("krok")
                .apply(screen);

            if (!(this.currentScreen instanceof CoreScreen)) {

                this.setCurrentScreen(
                    this.createScreen(screen)
                );

            }

        }

        // ============================================================
        // APPLY NOTICE SCREEN
        // ============================================================

        private applyNoticeScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // ============================================================
        // APPLY LIST SCREEN
        // ============================================================

        private applyListScreen(
            screen: Screen
        ): void {

            const state =
                this.store.getState();

            this.setCurrentScreen(

                this.createScreen(screen),

                {

                    ...state,

                    primaryScreen: {

                        type: "list",

                        from:
                            state.primaryScreen

                    },

                    completedProducts:
                        screen.products ?? [],

                    isListLoading: false,

                    isLoading: false

                }

            );

        }

        // ============================================================
        // APPLY ACCOUNT SCREEN
        // ============================================================

        private applyAccountScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }
        // ============================================================
        // APPLY SUBSCRIPTIONS SCREEN
        // ============================================================

        private applySubscriptionsScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // ============================================================
        // APPLY CHANGE SUBSCRIPTIONS SCREEN
        // ============================================================

        private applyChangeSubscriptionsScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // ============================================================
        // APPLY CONFIRM CANCEL SUBSCRIPTION SCREEN
        // ============================================================

        private applyConfirmCancelSubscriptionScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // ============================================================
        // APPLY CONFIRM CHANGE SCREEN
        // ============================================================

        private applyConfirmChangeScreen(
            screen: Screen
        ): void {

            this.applyMappedScreen(
                screen
            );

        }

        // =========================
        // SCREEN EVENTS
        // =========================

        private async onListClick(): Promise<void> {

            try {

                const res =
                    await this.api.getCompletedList();

                this.applyScreen(
                    res.screen
                );

            } catch (e) {

                console.error(e);

                alert(
                    "Ошибка загрузки списка"
                );

            }

        }

        private onBackClick(): void {
  console.log("ON BACK CLICK");          

            const state =
                this.store.getState();

            const screen =
                state.primaryScreen as any;

            if (screen.from) {

                if (state.krok) {

                    this.setCurrentScreen(
                        this.createScreen(state.krok)
                    );

                }

                this.store.update(prev => ({

                    ...prev,

                    primaryScreen:
                        screen.from

                }));

                return;

            }

            if (state.krok && state.currentKrokId) {

                this.store.update(prev => ({

                    ...prev,

                    primaryScreen: {
                        type: "core"
                    }

                }));

                return;

            }

            console.warn(
                "BACK FALLBACK → RELOAD"
            );

            window.location.reload();

        }

        private async onUserClick(): Promise<void> {

            try {

                const screen =
                    await this.api.getAccount();

                this.applyScreen(
                    screen
                );

            } catch (e) {

                console.error(e);

            }

        }

        private async onLogout(): Promise<void> {

            await this.auth.logout();

            window.location.reload();

        }


        private createRenderEngine(): RenderHost {

            return new RenderEngine(
                this.root
            );

        }

      private async boot(): Promise<void> {

          try {

            const route = this.getInitialRoute()

            // =========================
            // 🔥 AUTH FLOW (landing → subscribe)
            // =========================

            if (route.isAuth) {

              // если есть sticker → subscribe auth
              if (route.sticker) {

            const state = this.store.getState();

            this.store.replace({

                ...state,

                selectedSticker: route.sticker,

                referral:

                    route.referral && route.referral.length > 0

                        ? route.referral

                        : null,

                isBooting: false

            });

            this.applyScreen({

                type: "subscribeAuth"

            });

            window.history.replaceState(

                {},

                "",

                window.location.pathname

            );

            return;
              }

              // обычный auth
            const state = this.store.getState();

            this.store.replace({

                ...state,

                referral:

                    route.referral && route.referral.length > 0

                        ? route.referral

                        : null,

                isBooting: false

            });

            this.applyScreen({

                type: "auth"

            });

            window.history.replaceState(

                {},

                "",

                window.location.pathname

            );

            return;
        }
            // =========================
            // 🔥 NORMAL FLOW
            // =========================

            await this.auth.waitUntilAuthenticated()

            const res = await this.api.getEntry()

            this.applyScreen(res.screen)

          } catch {

            const state = this.store.getState();

            this.store.replace({

                ...state,

                isBooting: false

            });

            this.applyScreen({

                type: "auth"

            });

          }

      }

  
      private async onCompleteClick(): Promise<void> {

        const state = this.store.getState()

        if (!state.currentKrokId) return

        const res = await this.api.complete(state.currentKrokId)

        this.applyScreen(res.screen)
      }



      private async onPrevClick(): Promise<void> {

        const state = this.store.getState()

        const prevId = state.krok?.prev_krok_id

        if (!prevId) return

        const res = await this.api.navigate(prevId)
        this.applyScreen(res.screen)

      }

      private async onNextClick(): Promise<void> {

        const state = this.store.getState()

        const nextId = state.krok?.next_krok_id

        if (!nextId) return

        const res = await this.api.navigate(nextId)
        this.applyScreen(res.screen)

      }

      private async onEntryClick(): Promise<void> {

        const res = await this.api.entryKrok()
        this.applyScreen(res.screen)

      }

}